import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { Card, Modal, Tooltip } from '@components';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, ChartTooltip, Legend);

interface Props {
  data: any; // Structure complète avec données, metadata et aiAnalysis
}

export const AverageSalesByHourChart: React.FC<Props> = ({ data }) => {
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar');

  // Gestion sécurisée des données
  const safeData = data?.data || [];
  const aiAnalysis = data?.aiAnalysis;

  const chartData = useMemo(() => {
    const sortedData = [...safeData].sort((a, b) => {
      const hourA = parseInt(a.hour.split(':')[0]);
      const hourB = parseInt(b.hour.split(':')[0]);
      return hourA - hourB;
    });

    return {
      labels: sortedData.map((item) => item.hour),
      datasets: [
        {
          label: 'Revenus moyens par heure',
          data: sortedData.map((item) => item.averageRevenue),
          backgroundColor: chartType === 'bar' 
            ? 'rgba(99, 102, 241, 0.6)'
            : 'rgba(99, 102, 241, 0.1)',
          borderColor: 'rgba(99, 102, 241, 1)',
          borderWidth: 2,
          fill: chartType === 'line',
          tension: 0.4,
        },
      ],
    };
  }, [safeData, chartType]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            return `Revenus: ${formatNumber(value)} FCFA`;
          },
        },
      },
    },
    scales: {
      x: { 
        grid: { display: false },
        ticks: { 
          font: { size: 11 },
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          font: { size: 11 },
          callback: function(value: number) {
            return formatNumber(value) + ' FCFA';
          }
        },
        title: {
          display: true,
          text: 'Revenus moyens (FCFA)',
          font: { size: 12 },
        },
      },
    },
    elements: chartType === 'line' ? {
      point: {
        radius: 4,
        hoverRadius: 6,
      },
    } : {},
  }), [chartType]);

  // Fonction pour formater les grands nombres
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return Math.round(num).toString();
  };

  // Calculs des métriques
  const metrics = useMemo(() => {
    if (safeData.length === 0) return null;

    const revenues = safeData.map((item: any) => item.averageRevenue);
    const maxRevenue = Math.max(...revenues);
    const minRevenue = Math.min(...revenues);
    const avgRevenue = revenues.reduce((sum: number, rev: number) => sum + rev, 0) / revenues.length;
    
    const peakHour = safeData.find((item: any) => item.averageRevenue === maxRevenue);
    const lowHour = safeData.find((item: any) => item.averageRevenue === minRevenue);

    return {
      peakHour: peakHour?.hour,
      peakRevenue: maxRevenue,
      lowHour: lowHour?.hour,
      lowRevenue: minRevenue,
      averageRevenue: avgRevenue,
      totalHours: safeData.length
    };
  }, [safeData]);

  return (
    <div className="space-y-6">
      {/* Métriques de résumé */}
      {metrics && (
        <Card customClassName="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Tooltip content="L'heure de la journée générant le plus de revenus en moyenne">
              <div className="text-center p-4 bg-gradient-to-br from-indigo-50 to-blue-100 dark:from-indigo-900/30 dark:to-blue-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-indigo-200 dark:border-indigo-700">
                <div className="text-2xl mb-2">🏆</div>
                <h4 className="font-semibold text-indigo-800 dark:text-slate-100 mb-2">Heure de Pic</h4>
                <p className="text-xl font-bold text-indigo-900 dark:text-gray-50 mb-1">
                  {metrics.peakHour}
                </p>
                <p className="text-sm font-medium text-indigo-700 dark:text-stone-200">
                  {formatNumber(metrics.peakRevenue)} FCFA
                </p>
              </div>
            </Tooltip>

            <Tooltip content="L'heure avec la plus faible activité commerciale moyenne">
              <div className="text-center p-4 bg-gradient-to-br from-rose-50 to-red-100 dark:from-rose-900/30 dark:to-red-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-rose-200 dark:border-rose-700">
                <div className="text-2xl mb-2">📉</div>
                <h4 className="font-semibold text-rose-800 dark:text-zinc-100 mb-2">Heure Creuse</h4>
                <p className="text-xl font-bold text-rose-900 dark:text-neutral-50 mb-1">
                  {metrics.lowHour}
                </p>
                <p className="text-sm font-medium text-rose-700 dark:text-gray-200">
                  {formatNumber(metrics.lowRevenue)} FCFA
                </p>
              </div>
            </Tooltip>

            <Tooltip content="Revenus moyen généré par heure sur toute la journée">
              <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/30 dark:to-teal-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-emerald-200 dark:border-emerald-700">
                <div className="text-2xl mb-2">📊</div>
                <h4 className="font-semibold text-emerald-800 dark:text-white mb-2">Moyenne</h4>
                <p className="text-xl font-bold text-emerald-900 dark:text-slate-50">
                  {formatNumber(metrics.averageRevenue)} FCFA
                </p>
              </div>
            </Tooltip>

            <Tooltip content="Différence de performance entre l'heure la plus forte et la plus faible">
              <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-800/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-help border border-amber-200 dark:border-amber-700">
                <div className="text-2xl mb-2">📈</div>
                <h4 className="font-semibold text-amber-800 dark:text-stone-100 mb-2">Écart</h4>
                <p className="text-xl font-bold text-amber-900 dark:text-gray-100">
                  {formatNumber(metrics.peakRevenue - metrics.lowRevenue)} FCFA
                </p>
              </div>
            </Tooltip>
          </div>
        </Card>
      )}

      {/* Graphique principal */}
      <Card customClassName="p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Revenus Moyens par Heure
          </h3>
          <div className="flex items-center gap-3">
            {/* Sélecteur de type de graphique */}
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setChartType('bar')}
                className={`px-3 py-1 text-sm rounded-md transition-all ${
                  chartType === 'bar'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                📊 Barres
              </button>
              <button
                onClick={() => setChartType('line')}
                className={`px-3 py-1 text-sm rounded-md transition-all ${
                  chartType === 'line'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                📈 Courbe
              </button>
            </div>
            
            {aiAnalysis && (
              <button
                onClick={() => setShowAnalysisModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <span className="text-lg">🤖</span>
                Analyse IA
              </button>
            )}
          </div>
        </div>

        {safeData.length === 0 ? (
          <p className="text-center text-gray-500 italic py-8">Aucune donnée à afficher.</p>
        ) : (
          <div className="relative h-64 md:h-80">
            {chartType === 'bar' ? (
              <Bar data={chartData} options={options as any} />
            ) : (
              <Line data={chartData} options={options as any} />
            )}
          </div>
        )}

        <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-400 text-center">
            📋 <strong>Insight:</strong> Ce graphique révèle les patterns d'activité de votre business. 
            Les heures de pic vous aident à optimiser vos ressources et votre personnel.
          </p>
        </div>
      </Card>

      {/* Modal d'analyse IA */}
      <Modal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        title="🤖 Analyse IA - Revenus par Heure"
        customClassName="max-w-4xl"
      >
        {aiAnalysis && (
          <div className="space-y-6">
            {/* Badge de confiance */}
            <div className="flex justify-center">
              <span className="px-4 py-2 text-sm bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 rounded-full border border-purple-200">
                <span className="font-semibold">Niveau de confiance:</span> {aiAnalysis.confidence || 0}%
              </span>
            </div>

            {/* Analyse textuelle complète */}
            {aiAnalysis.analysis && (
              <div className="p-6 bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-800 dark:to-gray-800 rounded-xl shadow-sm border border-slate-300 dark:border-slate-600">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">📊</span>
                  <h5 className="font-semibold text-lg text-slate-800 dark:text-slate-100">
                    Analyse Détaillée des Patterns Horaires
                  </h5>
                </div>
                
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <div 
                    className="text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-line"
                    dangerouslySetInnerHTML={{ 
                      __html: aiAnalysis.analysis
                        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-indigo-600 dark:text-indigo-400">$1</strong>')
                        .replace(/### (.*?)(\n|$)/g, '<h3 class="text-lg font-bold text-slate-800 dark:text-slate-100 mt-4 mb-2">$1</h3>')
                        .replace(/---/g, '<hr class="my-4 border-slate-300 dark:border-slate-600">')
                        .replace(/- \*\*(.*?)\*\*/g, '<li class="ml-4"><strong class="text-emerald-600 dark:text-emerald-400">$1</strong></li>')
                    }}
                  />
                </div>
              </div>
            )}

            {/* Insights sous forme de cartes */}
            {aiAnalysis.insights && aiAnalysis.insights.length > 0 && (
              <div>
                <h4 className="text-lg font-bold text-green-400 mb-4">
                  🧠 Points Clés Identifiés
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {aiAnalysis.insights.map((insight: string, index: number) => (
                    <div key={index} className="p-4 bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-800/40 rounded-xl shadow-sm border border-green-200 dark:border-green-700">
                      <div className="flex items-start gap-3">
                        <span className="text-green-600 dark:text-green-400 mt-1 text-lg">💡</span>
                        <p className="text-sm text-green-800 dark:text-green-200 leading-relaxed">
                          {insight}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommandations */}
            {aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0 && (
              <div>
                <h4 className="text-lg font-bold text-orange-400 mb-4">
                  💡 Recommandations d'Optimisation
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  {aiAnalysis.recommendations.map((recommendation: string, index: number) => (
                    <div key={index} className="p-4 bg-gradient-to-br from-orange-50 to-amber-100 dark:from-orange-900/30 dark:to-amber-800/40 rounded-xl shadow-sm border border-orange-200 dark:border-orange-700">
                      <div className="flex items-start gap-3">
                        <span className="text-orange-600 dark:text-orange-400 mt-1 text-lg">🎯</span>
                        <p className="text-sm text-orange-800 dark:text-orange-200 leading-relaxed">
                          {recommendation}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Informations sur l'analyse */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Analyse générée automatiquement par IA
                </span>
                <span>
                  {aiAnalysis.generatedAt ? new Date(aiAnalysis.generatedAt).toLocaleDateString('fr-FR') : ''} à {aiAnalysis.generatedAt ? new Date(aiAnalysis.generatedAt).toLocaleTimeString('fr-FR') : ''}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
